import React, { type ReactNode } from "react"
import { View } from "react-native"
import Spacer from "@/components/util/Spacer"
import WidgetNavIcon from "@/assets/clipart/widgets/widget-nav-icon.svg"
import { type SvgProps } from "react-native-svg"
import BFText, { Body, H1, H4, Small } from "@/components/standard/BFText"
import { WidgetTitleRow } from "@/components/widgets/WidgetWrapper"

interface WidgetCommonContainerProps {
  headerCount?: string
  subTitle: string
  ctaText?: string
  children?: ReactNode
  variant?: "default" | "new"
}

const WidgetCommonContainer: React.FC<WidgetCommonContainerProps> = ({
  headerCount,
  subTitle,
  ctaText,
  children,
  variant = "default",
}) => {
  return (
    <View className="items-start justify-start flex-1 mt-2">
      <H1
        className={`${variant === "new" ? "text-midnight-blue" : "text-white"}`}
      >
        {headerCount ?? "--"}
      </H1>
      <Small
        className={`${variant === "new" ? "text-midnight-blue" : "text-white"} text-[13px]`}
      >
        {subTitle}
      </Small>
      {ctaText && (
        <>
          <Spacer />
          <BFText
            className={`${variant === "new" ? "text-midnight-blue" : "text-white"} text-[15px] w-full font-figtree-extrabold tracking-tight mb-3`}
          >
            {ctaText}
          </BFText>
        </>
      )}
      {children}
    </View>
  )
}

export default WidgetCommonContainer

interface WidgetCommonFTUContainerProps {
  headerText: string
  subTitle?: string
  TopComponent?: ReactNode | null
  BottomIconComponent?: React.FC<SvgProps>
  variant?: "default" | "new"
}

export const WidgetCommonFTUContainer: React.FC<
  WidgetCommonFTUContainerProps
> = ({
  subTitle,
  headerText,
  TopComponent = null,
  BottomIconComponent,
  variant = "default",
}) => {
  return (
    <View className="flex-1 w-full">
      {TopComponent}
      <H4
        className={`${variant === "new" ? "text-midnight-blue mt-2" : "text-white mt-4"}`}
      >
        {headerText}
      </H4>
      {subTitle && (
        <Small
          className={`${variant === "new" ? "text-midnight-blue" : "text-white"} mt-2`}
        >
          {subTitle}
        </Small>
      )}
      <Spacer />
      {BottomIconComponent && (
        <View className="items-end">
          <BottomIconComponent
            style={{
              marginRight: 2,
              marginBottom: 10,
            }}
          />
        </View>
      )}
    </View>
  )
}

interface WidgetCommonHorizontalContainerProps {
  layoutType: "short" | "tall"
  IconComponent: React.ElementType
  ImageComponent: React.ElementType
  headerCount?: string
  iconCopy?: string
  title?: string
  subTitle: string
  ctaText: string
}

export const WidgetCommonHorizontalContainer: React.FC<
  WidgetCommonHorizontalContainerProps
> = ({
  layoutType = "short",
  IconComponent,
  ImageComponent,
  headerCount,
  iconCopy,
  title = null,
  subTitle,
  ctaText,
}) => {
  return (
    <View className="relative w-full">
      {layoutType === "short" ? (
        // Short layout type
        <View className="flex-row justify-center items-center px-[14px] py-[10px]">
          <IconComponent />
          <View className="flex flex-1 items-start justify-start ml-1">
            {headerCount && (
              <BFText className="text-white h-[40px] text-[38px] font-figtree-extrabold leading-none">
                {headerCount}
              </BFText>
            )}
            <H4 className="text-white w-full">{subTitle}</H4>
            <Small className={"text-white"}>{ctaText}</Small>
          </View>
          <View className="h-full">
            <WidgetNavIcon />
          </View>
        </View>
      ) : (
        // Tall layout type
        <View>
          {iconCopy && (
            <WidgetTitleRow
              title={iconCopy}
              ctaText={ctaText}
              category={"marketing"}
              IconComponentOverride={IconComponent}
            />
          )}
          <View className="items-start justify-start px-[12px] max-w-[260px] pb-5">
            {headerCount && (
              <BFText className="text-white text-[38px] font-figtree-extrabold leading-none">
                {headerCount}
              </BFText>
            )}
            <H4 className="text-white mb-1">{title}</H4>
            <Body className="text-white">{subTitle}</Body>
          </View>
          <View className="absolute right-3 bottom-3">
            <ImageComponent width={35} height={35} />
          </View>
        </View>
      )}
    </View>
  )
}
